import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

const OrganizationEditor = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { userInfo, isAdmin, isSuperAdmin, isLoading: isUserLoading } = useUser();
  const userOrganizationId = userInfo?.organizationId;
  const navigate = useNavigate();
  const { id: organizationId } = useParams();
  const notification = useNotification();

  const isEditMode = !!organizationId;
  // Determine if user is editing their own org (admin) or any org (super admin)
  const isEditingOwnOrg = isEditMode && organizationId === userOrganizationId;

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: ''
  });

  useEffect(() => {
    // Super admins can access all; admins can only edit their own org
    if (!isUserLoading) {
      if (!isSuperAdmin() && !isAdmin()) {
        notification.error('You do not have permission to access this page');
        navigate('/dashboard');
      } else if (!isSuperAdmin() && isEditMode && !isEditingOwnOrg) {
        notification.error('You can only edit your own organization');
        navigate('/dashboard');
      } else if (!isSuperAdmin() && !isEditMode) {
        // Non-super-admins cannot create new organizations
        notification.error('Only Super Admins can create new organizations');
        navigate('/dashboard');
      }
    }
  }, [isAdmin, isSuperAdmin, isUserLoading, navigate, notification, isEditMode, isEditingOwnOrg]);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!organizationId) return;

      try {
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/organizations/${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setFormData({
          name: response.data.name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          website: response.data.website || ''
        });
      } catch (error) {
        console.error('Error fetching organization:', error);
        notification.error('Failed to load organization');
        navigate(isSuperAdmin() ? '/settings/organizations' : '/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, getAccessTokenSilently]);

  const handleCancel = () => {
    if (isSuperAdmin()) {
      navigate('/settings/organizations');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData.name.trim()) {
      notification.error('Organization name is required');
      return;
    }

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();

      if (isEditMode) {
        await axios.patch(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/organizations/${organizationId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notification.success('Organization updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/organizations`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notification.success('Organization created successfully');
      }

      if (isSuperAdmin()) {
        navigate('/settings/organizations');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      notification.error(error.response?.data?.error || 'Failed to save organization');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/organizations/${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Organization deleted successfully');
      navigate('/settings/organizations');
    } catch (error) {
      console.error('Error deleting organization:', error);
      notification.error(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const getPageTitle = () => {
    if (isEditMode) {
      return isEditingOwnOrg ? 'Organization Settings' : 'Edit Organization';
    }
    return 'Create New Organization';
  };

  const getPageSubtitle = () => {
    if (isEditMode) {
      return isEditingOwnOrg
        ? 'Manage your organization profile and details'
        : 'Update organization details';
    }
    return 'Add a new organization to the platform';
  };

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <button className="settings-back-btn" onClick={handleCancel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6" />
              </svg>
              Back
            </button>
            <h1 className="settings-page-title">{getPageTitle()}</h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading organization details...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Organization"
        description={`Are you sure you want to delete "${formData.name}"? This action cannot be undone. All users, locations, products, and flyers associated with this organization will be permanently deleted.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <button className="settings-back-btn" onClick={handleCancel}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Back
          </button>
          <h1 className="settings-page-title">{getPageTitle()}</h1>
          <p className="settings-page-subtitle">{getPageSubtitle()}</p>
        </div>
      </div>

      <div className="settings-page-content">
        <form onSubmit={handleSubmit} id="org-form">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3 className="settings-card-title">Organization Details</h3>
            </div>
            <div className="settings-card-body">
              <div className="settings-form-grid">
                <div className="settings-form-group full-width">
                  <label className="settings-form-label">
                    Organization Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="settings-form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter organization name"
                    required
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Email</label>
                  <input
                    type="email"
                    className="settings-form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="organization@example.com"
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-form-label">Phone</label>
                  <input
                    type="tel"
                    className="settings-form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div className="settings-form-group full-width">
                  <label className="settings-form-label">Website</label>
                  <input
                    type="url"
                    className="settings-form-input"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="settings-action-bar">
        <button
          type="button"
          className="settings-btn secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="org-form"
          className="settings-btn primary"
          disabled={isSaving || !formData.name.trim()}
        >
          {isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
        </button>
        {isEditMode && isSuperAdmin() && (
          <button
            type="button"
            className="settings-btn danger"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default OrganizationEditor;
